import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { Membership } from '../membership';
import { FormAnswer } from './form-answer';
import { Owner } from '../owner';
import { Unit } from '../unit';
import { Tag } from '../tag';
import { FormSubmissionComment } from './form-submission-comment';
import { Form } from './form';
import moment from 'moment-timezone';
import { FormSubmissionApproval } from './form-submission-approval';
import { PaginationMeta } from '../pagination';
import { FormQuestion } from './form-question';

export class FormSubmission
{
    id: number;
    organizationId: number;
    formId: number;
    membershipId: number;
    membership: Membership;
    unitId: number;
    unit: Unit;
    answers: FormAnswer[] = [];
    comments: FormSubmissionComment[] = [];
    status: string;
    formSubmissionStatusId: number;
    tags: Tag[] = [];
    completionDate: moment.Moment;
    createdAt: moment.Moment;
    ifShowApprovalsBasedOnDate: boolean;
    hasMemberApprovedSubmission: boolean;
    form: Form;
    approvals: FormSubmissionApproval[];

    approvalsGiven = 0;

    /** Requests V3 §5.4 — lean vendor assignment. Null until core lands the column; treated as unassigned. */
    assignedVendorId: number | null = null;

    constructor(request: any) {
        this.id = request.id;
        this.organizationId = request.organizationId;
        this.membershipId = request.membershipId;
        this.assignedVendorId = request.assignedVendorId ?? null;
        this.formId = request.formId;
        this.unitId = request.unitId;

        const timezone = LegFiJwtService.getTimezone();

        this.createdAt = moment.utc(request.createdAt).tz(timezone);
        this.completionDate = request.completionDate ? moment.utc(request.completionDate).tz(timezone) : null;

        // approval workflow didn't exist before this date so data wasn't collected
        this.ifShowApprovalsBasedOnDate = this.createdAt.isSameOrAfter(moment('2021-07-08'));

        // putting this in for now until we fix it on model saves...
        if (request.status && (request.status === 'complete' || request.status === 'pending')) {
            this.status = 'No Status';
        } else {
            this.status = request.status ?? 'No Status';
        }

        this.formSubmissionStatusId = request.formSubmissionStatusId;

        if (request.answers) {
            this.answers = request.answers.map(a => new FormAnswer(a));
        }

        if (request.tags) {
            this.tags = request.tags.map(a => new Tag(a));
        }

        if (request.comments) {
            this.comments = request.comments.map((a) => new FormSubmissionComment(a));
        }

        if (request.unit) {
            this.unit = new Unit(request.unit);
        }

        if (request.membership) {
            this.membership = new Membership(request.membership);
        }

        if (request.approvals) {
            this.approvals = request.approvals.map((a) => new FormSubmissionApproval(a));
        }

        if (request.approvalsGiven) {
            this.approvalsGiven = request.approvalsGiven || 0;
        }

        this.hasMemberApprovedSubmission = request.hasMemberApprovedSubmission;

        this.form = request.form ? new Form(request.form) : null;
    }

    // tags display format per new design
    // i.e. Tag 1 • Tag 2
    get tagsOneLine() {
        if (this.tags.length > 0) {
            return this.tags.map((tag) => tag.tag).join(' • ');
        }

        return '--';
    }
}

/** Class to extend form submission with some searchable, filterable fields */
export class FormSubmissionFlat extends FormSubmission
{
    submittedDate: moment.Moment;
    completionDate: moment.Moment;
    unitName: string;
    unitTags: Tag[];
    ownerName: string;
    status: string;
    form: Form;
    formattedAnswers: { id: number, questionText: string; answerText: string }[];
    formAnswerString: string;
    questions: FormQuestion[];
    submissionQuestionAnswerPairs: {question: string, answer: string}[];
    comments: FormSubmissionComment[];

    constructor(request: any) {
        super(request);

        this.submittedDate = request.createdAt;
        this.completionDate = request.completionDate || null;

        // putting this in for now until we fix it on model saves...
        if (request.status && (request.status === 'complete' || request.status === 'pending')) {
            this.status = 'No Status';
        } else {
            this.status = request.status ?? 'No Status';
        }

        if (request.unit) {
            const unit = new Unit(request.unit);
            this.unitName = unit.name;
            this.unitTags = unit.tags || [];

            if (unit.owners && unit.owners.length) {
                this.ownerName = unit.owners.map((o: Owner) => o.name).toString().toLocaleLowerCase();
            }
        }

        if (request.form) {
            this.form = request.form;
        }

        if (request.answers) {
            this.formattedAnswers = request.answers.map((a) => {
                const answer = new FormAnswer(a);
                let answerValue = answer.answer;

                const q = answer.question;
                if (q.type === 'hr' || q.type === 'plaintext') {
                    return null;
                }

                if (q.type === 'file') {
                    answerValue = '(file)';
                }
                if (q.type === 'checkbox') {
                    answerValue = answer.answer === 'true' || answer.answer === '1' ? 'Yes' : 'No';
                }
                if (q.type === 'input' || q.type === 'textarea') {
                    answerValue = answer.answer;
                }
                if (q.type === 'select') {
                    if (a.answer.split(',').length > 1) {
                        const values = [];
                        for (const value of a.answer.split(',')) {
                            const option = q.options.find(o => o.value === value);
                            if (option) {
                                values.push(option.label);
                            }
                        }
                        answerValue = values.join(', ');
                    } else {
                        answerValue = q.options.find(o => o.value === a.answer)?.label || '';
                    }
                }

                    return {
                        id: answer.question.id,
                        questionText: answer.question.label,
                        answerText: answerValue,
                    };
                })
                .filter((a) => a !== null); // Filter out null values from hr/plaintext questions

            // strip out HTML tags and some entities for answer string
            this.formAnswerString = this.formattedAnswers.map((a) => a.answerText).toString()
                    .replace(/<[^>]+>/g, '')
                    .replace(/&(nbsp|amp|quot|lt|gt);/g, '')
                    .toLocaleLowerCase();
        }

        // Extract questions from form.questions (loaded from backend)
        if (request.form?.questions) {
            this.questions = request.form.questions.map((q) => new FormQuestion(q));
        } else if (request.questions) {
            // Fallback to direct questions if available
            this.questions = request.questions.map((q) => new FormQuestion(q));
        }

        if(request.comments) {
            this.comments = request.comments;
        }
    }

    get title(): string {
        if (!this.questions || this.questions.length === 0) {
            return '';
        }

        const question = this.questions[0];
        if (!question) {
            return '';
        }

        if (!this.answers || this.answers.length === 0) {
            return '';
        }

        const answer = this.answers.find((a) => a.formQuestionId === question.id)?.answer;
        if (!answer) {
            return '';
        }

        return question.label + ' ' + answer.toString()
                .replace(/<[^>]+>/g, '')
                .replace(/&(nbsp|amp|quot|lt|gt);/g, '');
    }
}

export class FormSubmissionResponse
{
    formSubmissionFlats: FormSubmissionFlat[];
    data: FormSubmissionFlatCollection;
    meta: PaginationMeta;

    constructor(request: any) {
        this.formSubmissionFlats = (request.data && request.data.length) ? request.data.map((c: any): FormSubmissionFlat => {
            return new FormSubmissionFlat(c);
        }) : [];

        this.data = new FormSubmissionFlatCollection(this.formSubmissionFlats);
        this.meta = new PaginationMeta(request);
    }
}

export class FormSubmissionFlatCollection
{
    formSubmissionFlats: FormSubmissionFlat[];
    allQuestions: { id: number, label: string }[];
    // submissionQuestionAnswerPairs: any;
    submissionQuestionAnswerPairs: {question: string, answer: string}[][];

    constructor(formSubmissionFlats: FormSubmissionFlat[]) {
        // 1. Gather all unique questions (by id and label)
        const questionMap = new Map<number, string>();
        formSubmissionFlats.forEach((formSubmissionFlat: FormSubmissionFlat) => {
            (formSubmissionFlat.formattedAnswers || []).forEach((formattedAnswer: { id: number, questionText: string; answerText: string }) => {

                if(formattedAnswer.id && formattedAnswer.questionText) {
                    questionMap.set(formattedAnswer.id, formattedAnswer.questionText);
                }
            });
        });
        this.allQuestions = Array.from(questionMap.entries()).map(
                ([id, label]) => ({ id, label})
        );
        // 2. For each submission, build question/answer pairs for all questions
        this.submissionQuestionAnswerPairs =
        formSubmissionFlats.map(formSubmissionFlat => {
            const answerMap = new Map<number, string|null>();
            (formSubmissionFlat.formattedAnswers || []).forEach((formattedAnswer: { id: number, questionText: string; answerText: string }) => {
                if(formattedAnswer.id) {
                    answerMap.set(formattedAnswer.id, formattedAnswer.answerText ?? null);
                }
            });

            formSubmissionFlat.submissionQuestionAnswerPairs = [];

            this.allQuestions.forEach(q => {
                formSubmissionFlat.submissionQuestionAnswerPairs.push({
                    question: q.label,
                    answer: answerMap.has(q.id) ? answerMap.get(q.id) : null
                });
            });

            return this.allQuestions.map(q => ({
                question: q.label,
                answer: answerMap.has(q.id) ? answerMap.get(q.id) : null
            }));
        });

        this.formSubmissionFlats = formSubmissionFlats;
    }
}
